<template>
  <div class="container">
    <el-card>
      <template #header>
        <div class="card-header">
          <h2>إصدار بطاقات هوية الطلاب</h2>
        </div>
      </template>

      <div class="filter-section">
        <el-select v-model="filters.class_id" placeholder="اختر الفصل" @change="onClassChange" clearable>
          <el-option v-for="cls in classes" :key="cls.id" :label="cls.name" :value="cls.id" />
        </el-select>
        <el-select v-model="filters.section_id" placeholder="اختر الشعبة" @change="handleFilterChange" :disabled="!filters.class_id" clearable>
          <el-option v-for="sec in sections" :key="sec.id" :label="sec.name" :value="sec.id" />
        </el-select>
        <el-input 
          v-model="filters.search"
          placeholder="ابحث بالاسم أو رقم القيد..."
          @input="handleSearch"
          clearable
        />
      </div>
      
      <div class="bulk-actions" v-if="selectedStudents.length > 0">
        <el-button type="success" @click="printSelectedCards">
          <el-icon><Printer /></el-icon> 
          طباعة البطاقات المحددة ({{ selectedStudents.length }})
        </el-button>
      </div>

      <el-table :data="students" v-loading="loading" border style="width: 100%" @selection-change="handleSelectionChange">
        <el-table-column type="selection" width="55" />
        <el-table-column prop="srn" label="رقم القيد" width="120" />
        <el-table-column prop="name" label="اسم الطالب (الإنجليزي)" />
        <el-table-column prop="arabic_name" label="اسم الطالب (العربي)" />
        <el-table-column label="الإجراءات" width="180" align="center">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="printCard(row)">
              <el-icon><Printer /></el-icon> طباعة البطاقة
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-container">
        <el-pagination
          v-if="pagination.total > 0"
          @current-change="handlePageChange"
          :current-page="pagination.current_page"
          :page-size="pagination.per_page"
          :total="pagination.total"
          layout="total, prev, pager, next"
          background
        />
      </div>
    </el-card>
  </div>
</template>

<script>
import axios from 'axios';
import { ElMessage } from 'element-plus';
import { Printer } from '@element-plus/icons-vue';
import debounce from 'lodash/debounce';

const api = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api',
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

export default {
  name: 'StudentIdCards',
  components: { Printer },
  data() {
    return {
      filters: { 
        class_id: null, 
        section_id: null,
        search: '',
      },
      pagination: {
        current_page: 1,
        per_page: 10,
        total: 0,
      },
      classes: [],
      sections: [],
      students: [],
      loading: false,
      selectedStudents: [],
    };
  },
  created() {
    this.fetchClasses();
  },
  methods: {
    async fetchClasses() {
      try {
        const response = await api.get('/classes');
        this.classes = response.data;
      } catch (error) {
        ElMessage.error('فشل في جلب الفصول الدراسية.');
      }
    },
    async onClassChange(classId) {
      this.filters.section_id = null;
      this.sections = [];
      if (classId) {
        try {
          const response = await api.get(`/classes/${classId}/sections-to`);
          this.sections = response.data.sections || response.data;
          this.handleFilterChange();
        } catch (error) {
          ElMessage.error('فشل في جلب الشعب.');
        }
      } else {
        this.handleFilterChange();
      }
    },
    async fetchStudents() {
      if (!this.filters.class_id) {
        this.students = [];
        this.pagination.total = 0;
        return;
      }
      this.loading = true;
      try {
        const params = {
          ...this.filters,
          page: this.pagination.current_page,
          per_page: this.pagination.per_page,
        };
        const response = await api.get('/students', { params });
        this.students = response.data.data;
        this.pagination.total = response.data.total;
        this.pagination.current_page = response.data.current_page;
      } catch (error) {
        ElMessage.error('فشل في جلب الطلاب.');
      } finally {
        this.loading = false;
      }
    },
    handleFilterChange() {
      this.pagination.current_page = 1;
      this.fetchStudents();
    },
    handleSearch: debounce(function() {
      this.handleFilterChange();
    }, 500),
    handlePageChange(newPage) {
      this.pagination.current_page = newPage;
      this.fetchStudents();
    },
    handleSelectionChange(selection) {
      this.selectedStudents = selection;
    },
    generateQrData(student) {
      const pinCode = student.parent ? student.parent.pin_code : null;
      const qrCodeArray = ["student", String(student.id), String(pinCode)];
      return JSON.stringify(qrCodeArray);
    },
    printSelectedCards() {
      if (this.selectedStudents.length === 0) return;
      
      const cardsHtml = this.selectedStudents.map(student => this.generateCardHtml(student)).join('');
      const qrScripts = this.selectedStudents.map(student => {
        const qrCodeData = this.generateQrData(student);
        return `
          try {
            var qr = qrcode(4, 'L');
            qr.addData(${JSON.stringify(qrCodeData)});
            qr.make();
            document.getElementById('qr-container-${student.id}').innerHTML = qr.createImgTag(3, 2);
          } catch(e) { console.error(e); }
        `;
      }).join('');

      const printContent = this.generatePrintPageHtml(cardsHtml, qrScripts);
      const printWindow = window.open('', '_blank');
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
    },
    generateCardHtml(student) {
      const logoUrl = `${window.location.origin}/logo-school-one.png`;
      const studentPhotoUrl = student.photo 
        ? `${process.env.VUE_APP_API_BASE_URL.replace('/api', '')}/storage/${student.photo}`
        : `${window.location.origin}/default-student-avatar.png`;

      return `
        <div class="card-wrapper">
          <div class="card">
            <div class="card-header">
              <img src="${logoUrl}" alt="School Logo">
              <span>Caledonian International School</span>
            </div>
            <div class="card-body">
              <div class="student-photo-section">
                <img src="${studentPhotoUrl}" class="student-photo" alt="Student Photo">
              </div>
              <div class="student-info-section">
                <div class="name-container">
                  <div class="name-arabic">${student.arabic_name || student.name}</div>
                  <div class="name-english">${student.name}</div>
                </div>
                <div class="student-details">
                  <div class="detail-item"><span class="label">SRN:</span> ${student.srn}</div>
                  <div class="detail-item"><span class="label">Class:</span> ${student.class ? student.class.name : ''} - ${student.section ? student.section.name : ''}</div>
                </div>
              </div>
              <div class="qr-section">
                <div id="qr-container-${student.id}"></div>
              </div>
            </div>
            <div class="card-footer">
              Academic Year 2024-2025
            </div>
          </div>
        </div>
      `;
    },
    generatePrintPageHtml(cardsHtml, qrScripts) {
        return `
        <html>
          <head>
            <title>Student ID Cards</title>
            <script src="https://cdn.jsdelivr.net/npm/qrcode-generator/qrcode.js"><\/script>
            <style>
              @import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@400;500;700&display=swap');
              body { 
                font-family: 'Tajawal', sans-serif; 
                margin: 0; 
                background-color: #f5f5f5; 
              }
              .print-page { 
                display: grid; 
                grid-template-columns: repeat(2, 1fr); 
                gap: 15px; 
                padding: 20px; 
              }
              .card-wrapper { 
                border: 1px dashed #999; 
                padding: 5px; 
                page-break-inside: avoid; 
                background-color: white; 
              }
              .card { 
                width: 420px; 
                height: 240px; 
                background-color: white; 
                border-radius: 16px; 
                display: flex; 
                flex-direction: column; 
                position: relative; 
                overflow: hidden; 
                box-shadow: 0 4px 8px rgba(0,0,0,0.1); 
              }
              .card-header { 
                background: linear-gradient(135deg, #00529b, #002d55); 
                color: white; 
                padding: 8px 15px; 
                display: flex; 
                align-items: center; 
                flex-shrink: 0; 
              }
              .card-header img { 
                width: 32px; 
                height: 32px; 
                margin-right: 7px; 
              }
              .card-header span { 
                font-size: 13px; 
                font-weight: 700; 
                letter-spacing: 0.5px; 
              }
              .card-body { 
                display: flex; 
                padding: 15px; 
                flex-grow: 1; 
                align-items: stretch; 
                gap: 12px;
              }
              .student-photo-section { 
                flex-shrink: 0; 
                display: flex; 
                align-items: flex-start; 
              }
              .student-photo { 
                width: 80px; 
                height: 80px; 
                border-radius: 10px; 
                border: 3px solid #00529b; 
                object-fit: cover; 
              }
              .student-info-section { 
                flex: 1; 
                display: flex; 
                flex-direction: column; 
                justify-content: space-between;
                min-width: 0;
                overflow: hidden;
              }
              .name-container {
                flex: 1;
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                min-height: 0;
              }
              .name-arabic { 
                font-size: 16px; 
                color: #333; 
                font-weight: 700; 
                direction: rtl; 
                text-align: right; 
                line-height: 1.3; 
                word-wrap: break-word; 
                overflow-wrap: break-word; 
                hyphens: auto;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                overflow: hidden;
                margin-bottom: 5px;
                min-height: 2.6em;
              }
              .name-english { 
                font-size: 13px; 
                font-weight: 500; 
                color: #002d55; 
                line-height: 1.2; 
                word-wrap: break-word; 
                overflow-wrap: break-word; 
                hyphens: auto;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                overflow: hidden;
                min-height: 2.4em;
                max-height: 2.4em;
              }
              .student-details { 
                margin-top: 8px; 
                flex-shrink: 0;
              }
              .detail-item { 
                font-size: 11px; 
                color: #555; 
                line-height: 1.4; 
                word-wrap: break-word; 
                overflow-wrap: break-word; 
              }
              .detail-item .label { 
                font-weight: 600; 
                display: inline-block;
                min-width: 40px;
              }
              .qr-section { 
                flex-shrink: 0; 
                display: flex; 
                align-items: flex-start; 
              }
              [id^=qr-container] { 
                width: 75px; 
                height: 75px; 
              }
              [id^=qr-container] img { 
                width: 100% !important; 
                height: 100% !important; 
              }
              .card-footer { 
                background-color: #e0e7ed; 
                font-size: 10px; 
                text-align: center; 
                padding: 5px 15px; 
                color: #555; 
                flex-shrink: 0; 
                border-top: 1px solid #d0d7e0; 
              }
              
              /* تحسينات للطباعة */
              @media print {
                body { 
                  background-color: white; 
                  margin: 0;
                  padding: 0;
                }
                .print-page { 
                  padding: 5mm; 
                  gap: 10px;
                  display: grid;
                  grid-template-columns: repeat(2, 1fr);
                }
                .card-wrapper { 
                  border: 1px solid #ddd; 
                  page-break-inside: avoid;
                  break-inside: avoid;
                }
                .card { 
                  box-shadow: none; 
                  border: 1px solid #ccc;
                  width: 100%;
                  height: auto;
                  min-height: 220px;
                }
                .card-body {
                  padding: 12px;
                  gap: 10px;
                }
                .student-photo {
                  width: 70px;
                  height: 70px;
                }
                [id^=qr-container] {
                  width: 70px;
                  height: 70px;
                }
                .name-arabic {
                  font-size: 15px;
                  -webkit-line-clamp: 2;
                }
                .name-english {
                  font-size: 12px;
                  -webkit-line-clamp: 2;
                }
              }
            </style>
          </head>
          <body>
            <div class="print-page">${cardsHtml}</div>
            <script>${qrScripts}<\/script>
          </body>
        </html>
      `;
    },
    printCard(student) {
      const cardHtml = this.generateCardHtml(student);
      const qrCodeData = this.generateQrData(student);
      const qrScript = `
        try {
          var qr = qrcode(4, 'L');
          qr.addData(${JSON.stringify(qrCodeData)});
          qr.make();
          document.getElementById('qr-container-${student.id}').innerHTML = qr.createImgTag(3, 2);
        } catch(e) { console.error(e); }
      `;
      const printContent = this.generatePrintPageHtml(cardHtml, qrScript);
      const printWindow = window.open('', '_blank');
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => { 
        printWindow.print(); 
        printWindow.close(); 
      }, 500);
    }
  }
};
</script>

<style scoped>
.container {
  margin: 20px;
}
.filter-section {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
}
.bulk-actions {
  margin-bottom: 20px;
}
.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}
</style>